import { ApiResponse } from '@/utils';
import { enhancedRequest } from '@/utils/enhancedRequest';
import { handleSmsError } from '@/utils/errorHandler';
import {getInviteCode, getUtmSource} from "@/utils/auth"
import {
  SmsLoginParams,
  SmsLoginResponse,
  PasswordLoginParams,
  PasswordLoginResponse,
} from '../types';

/**
 * AMAI认证服务
 */
class AuthService {
  /**
   * 根据Ticket获取用户Token（带防重复请求）
   * @param ticket 临时登录标识
   * @returns {token: string, user_id: string, user_name: string, user_avatar: string}
   */
  async getTokenByTicket(ticket: string): Promise<
    ApiResponse<{
      token: string;
      user_id: string;
      user_name: string;
      user_avatar: string;
      mobile?: string;
    }>
  > {
    return enhancedRequest.get<{
      token: string;
      user_id: string;
      user_name: string;
      user_avatar: string;
      mobile?: string;
    }>(
      '/auth-token',
      { ticket },
      {
        enableDeduplication: true,
        requestKey: `auth-token:${ticket}`,
        retryCount: 0,
        timeout: 10000, // 认证请求10秒超时
      }
    );
  }

  /**
   * 获取用户个人信息（带防重复请求和缓存）
   * @param forceRefresh 是否强制刷新缓存
   * @returns {user_id: string, user_name: string, user_avatar: string}
   */
  async getUserProfile(forceRefresh: boolean = false): Promise<
    ApiResponse<{
      user_id: string;
      user_name: string;
      user_avatar: string;
      mobile?: string;
      auth_identifier?: string;
      wechat?: string;
      show_new_user_gift?: boolean;
      /** 个人账号 user_id（团队 token 下也返回，空间切换比对用） */
      personal_user_id?: string;
    }>
  > {
    return enhancedRequest.post<{
      user_id: string;
      user_name: string;
      user_avatar: string;
      mobile?: string;
      auth_identifier?: string;
      wechat?: string;
      show_new_user_gift?: boolean;
      personal_user_id?: string;
    }>(
      '/user/profile',
      {},
      {
        useCache: true,
        cacheTTL: 10 * 60 * 1000, // 用户信息缓存10分钟
        forceRefresh, // 支持强制刷新缓存
        enableDeduplication: true,
        requestKey: 'user-profile',
        retryCount: 2,
      }
    );
  }

  /**
   * 短信验证码登录
   * @param params 请求参数 {mobile: string, country_code?: string, sms_code: string}
   * @returns 登录响应数据
   */
  async smsLogin(params: SmsLoginParams): Promise<ApiResponse<SmsLoginResponse>> {
    try {
      const code = getInviteCode();
      // 设置默认国家区号
      const requestParams = {
        country_code: '86',
        ...params,
      };

      // 验证参数
      if (!requestParams.mobile) {
        throw new Error('手机号不能为空');
      }

      if (!requestParams.sms_code) {
        throw new Error('短信验证码不能为空');
      }

      const utm = getUtmSource();
      const urlParams = new URLSearchParams();
      if (code) urlParams.set('invite_code', code);
      if (utm) urlParams.set('utm_source', utm);
      const loginUrl = urlParams.toString() ? `/login?${urlParams.toString()}` : '/login';
      return await enhancedRequest.post<SmsLoginResponse>(loginUrl, requestParams, {
        enableDeduplication: true,
        requestKey: `sms-login:${requestParams.mobile}:${requestParams.sms_code}`,
        retryCount: 0, // 登录不重试，避免重复提交
        timeout: 15000, // 登录15秒超时
      });
    } catch (error) {
      // 使用专门的短信错误处理函数
      const errorInfo = handleSmsError(error, '短信验证码登录');
      throw new Error(errorInfo.message);
    }
  }

  /**
   * 账号密码登录
   * @param params 请求参数 {username: string, password: string}
   * @returns 登录响应数据
   */
  async passwordLogin(params: PasswordLoginParams): Promise<ApiResponse<PasswordLoginResponse>> {
    try {
      // 验证参数
      if (!params.auth_identifier) {
        throw new Error('用户名不能为空');
      } else {
        params.auth_identifier = params.auth_identifier.trim();
      }

      if (!params.auth_credential) {
        throw new Error('密码不能为空');
      } else {
        params.auth_credential = params.auth_credential.trim();
      }

      return await enhancedRequest.post<PasswordLoginResponse>('/auth-login', params, {
        enableDeduplication: true,
        requestKey: `password-login:${params.auth_identifier}`,
        retryCount: 0, // 登录不重试，避免重复提交
        timeout: 15000, // 登录15秒超时
      });
    } catch (error) {
      // 使用统一的错误处理
      console.log(error);
      const errorInfo = handleSmsError(error, '账号密码登录');
      throw new Error(errorInfo.message);
    }
  }

  /**
   * 退出登录
   * @returns 基础响应
   */
  async logout(): Promise<ApiResponse<any>> {
    return enhancedRequest.post<any>(
      '/user/logout',
      {},
      {
        enableDeduplication: true,
        requestKey: 'user-logout',
        retryCount: 1,
        timeout: 10000, // 退出登录10秒超时
      }
    );
  }
}

export default new AuthService();
